import { useCallback, useEffect, useMemo, useState } from 'react'

export type LeftPaneId = 'templates' | 'catalog' | 'layers'

type CollapsedPanes = Record<LeftPaneId, boolean>
type PaneHeights = {
    templates: number
    layers: number
}

type LeftPaneState = {
    collapsed: CollapsedPanes
    heights: PaneHeights
}

const STORAGE_KEY = 'leftPaneState'

const DEFAULT_COLLAPSED: CollapsedPanes = {
    templates: false,
    catalog: false,
    layers: false,
}

const DEFAULT_HEIGHTS: PaneHeights = {
    templates: 280,
    layers: 220,
}

export const LEFT_PANE_HEADER_HEIGHT = 47
export const LEFT_PANE_MIN_HEIGHT = 140
export const LEFT_PANE_TEMPLATES_MIN_HEIGHT = 280
export const LEFT_PANE_SPLITTER_HEIGHT = 6

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const minHeightForPane = (pane: keyof PaneHeights) =>
    pane === 'templates' ? LEFT_PANE_TEMPLATES_MIN_HEIGHT : LEFT_PANE_MIN_HEIGHT

const readStoredState = (): LeftPaneState => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) {
            return { collapsed: { ...DEFAULT_COLLAPSED }, heights: { ...DEFAULT_HEIGHTS } }
        }

        const parsed = JSON.parse(raw) as Partial<LeftPaneState>
        return {
            collapsed: {
                templates: Boolean(parsed.collapsed?.templates),
                catalog: Boolean(parsed.collapsed?.catalog),
                layers: Boolean(parsed.collapsed?.layers),
            },
            heights: {
                templates: clamp(
                    Number(parsed.heights?.templates) || DEFAULT_HEIGHTS.templates,
                    LEFT_PANE_TEMPLATES_MIN_HEIGHT,
                    800
                ),
                layers: clamp(Number(parsed.heights?.layers) || DEFAULT_HEIGHTS.layers, LEFT_PANE_MIN_HEIGHT, 800),
            },
        }
    } catch {
        return { collapsed: { ...DEFAULT_COLLAPSED }, heights: { ...DEFAULT_HEIGHTS } }
    }
}

/** Collapse + высоты левых панелей с персистом в localStorage (`leftPaneState`). */
export const useTemplateEditorLeftPaneState = () => {
    const [state, setState] = useState<LeftPaneState>(() => readStoredState())

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
        } catch {
            // ignore quota / private mode
        }
    }, [state])

    const togglePane = useCallback((pane: string) => {
        if (pane !== 'templates' && pane !== 'catalog' && pane !== 'layers') {
            return
        }

        setState((prev) => ({
            ...prev,
            collapsed: {
                ...prev.collapsed,
                [pane]: !prev.collapsed[pane],
            },
        }))
    }, [])

    const setPaneCollapsed = useCallback((pane: LeftPaneId, collapsed: boolean) => {
        setState((prev) => ({
            ...prev,
            collapsed: {
                ...prev.collapsed,
                [pane]: collapsed,
            },
        }))
    }, [])

    const setPaneHeight = useCallback((pane: keyof PaneHeights, height: number) => {
        setState((prev) => ({
            ...prev,
            heights: {
                ...prev.heights,
                [pane]: clamp(Math.round(height), minHeightForPane(pane), 800),
            },
        }))
    }, [])

    const paneHeights = useMemo(() => state.heights, [state.heights])
    const collapsedPanes = useMemo(() => state.collapsed, [state.collapsed])

    return {
        collapsedPanes,
        paneHeights,
        togglePane,
        setPaneCollapsed,
        setPaneHeight,
    }
}
